import axios from 'axios';
import { Track, Year, PredictionRequest, PredictionResponse, DriverDetails } from '../types/predictions';
import { getAccessToken } from './authToken';
import { traceparent } from '../utils/traceparent';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080';
// Legacy shared key, only used when no Auth0 access token is available (migration). Remove once the backend has
// AUTH0_DOMAIN / AUTH0_AUDIENCE set and API_KEY removed.
const API_KEY = process.env.REACT_APP_API_KEY;

/** Headers for backend calls: the logged-in user's Auth0 access token, or the legacy API key as a fallback. */
const authHeaders = async (): Promise<Record<string, string>> => {
  // traceparent: requests of one user action share a trace on the backend (see utils/traceparent.ts)
  const h: Record<string, string> = { 'Content-Type': 'application/json', traceparent: traceparent() };
  const token = await getAccessToken();
  if (token) {
    h.Authorization = `Bearer ${token}`;
  } else if (API_KEY) {
    h['X-API-Key'] = API_KEY;
  }
  return h;
};

export interface DriverDetailResponse extends DriverDetails {
  Driver: string;
}

export interface LoopData {
  Driver: string;
  St: number;
  DriverRating: number;
  Mid: number;
  High: number;
  Low: number;
  Avg: number;
  PassDif: number;
  GfPass: number;
  GfPassed: number;
  QPass: number;
  PctQPass: number;
  Fastest: number;
  T15Lap: number;
  PctT15Lap: number;
  Led: number;
  PctLed: number;
  Total: number;
  TotalRaces: number;
  Pos: number;
  Wins: number;
  Top5s: number;
  Top10s: number;
  WinPct: number;
  Top5Pct: number;
  Top10Pct: number;
  StPosDiff: number;
  [key: string]: string | number;
}

export interface DriverTrackStatsRequest {
  driver: string;
  year: number;
}

export interface DriverTrackStats {
  Site: string;
  St: number;
  DriverRating: number;
  Mid: number;
  High: number;
  Low: number;
  Avg: number;
  PassDif: number;
  GfPass: number;
  GfPassed: number;
  QPass: number;
  PctQPass: number;
  Fastest: number;
  T15Lap: number;
  PctT15Lap: number;
  Led: number;
  PctLed: number;
  Total: number;
  Pos: number;
}

/** Per-driver stats at a single track (inverse of driver-by-track rows). */
export type TrackDriverStatRow = {
  Driver: string;
  /** Rank of this track among this driver's tracks for the requested `rankStat` (1 = best). */
  TrackRank: number;
} & Omit<DriverTrackStats, 'Site'>;

export interface TrackDriverStatsRequest {
  track: string;
  year: number;
  /** Stat column used to rank each driver's tracks (must match backend loopdata field names). */
  rankStat?: string;
}

export const getDriverStats = async () => {
  return axios.get(`${API_URL}/api/stats/drivers`);
};

export const getRaceStats = async () => {
  return axios.get(`${API_URL}/api/stats/races`, { headers: await authHeaders() });
};

export const getTrackStats = async () => {
  return axios.get(`${API_URL}/api/stats/tracks`, { headers: await authHeaders() });
};

export const getTracks = async (): Promise<Track[]> => {
  const response = await axios.get(`${API_URL}/api/stats/tracks/list`, { headers: await authHeaders() });
  return response.data;
};

export const getYears = async (): Promise<Year[]> => {
  const response = await axios.get(`${API_URL}/api/stats/years`, { headers: await authHeaders() });
  return response.data;
};

export const getPredictions = async (request: PredictionRequest): Promise<PredictionResponse[]> => {
  const response = await fetch(`${API_URL}/api/predict/race`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      trackNames: request.trackNames,
      year: request.year,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to generate predictions');
  }

  const predictions = await response.json();
  
  // Ensure we only have one row per driver with aggregated stats
  const driverMap = new Map<string, PredictionResponse>();
  predictions.forEach((pred: PredictionResponse) => {
    driverMap.set(pred.driverName, pred);
  });
  
  return Array.from(driverMap.values());
};

export const getDriverDetails = async (
  drivers: string[],
  trackNames: string[],
  year: number
): Promise<DriverDetailResponse[]> => {
  const response = await fetch(`${API_URL}/api/stats/driver-details`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      drivers,
      trackNames,
      year,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver details');
  }

  return response.json();
};

export const getLoopData = async (request: PredictionRequest): Promise<LoopData[]> => {
  const response = await fetch(`${API_URL}/api/stats/loop-data`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      trackNames: request.trackNames,
      year: request.year,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch loop data');
  }

  return response.json();
};

export const getDriverTrackStats = async (request: DriverTrackStatsRequest): Promise<DriverTrackStats[]> => {
  const response = await fetch(`${API_URL}/api/stats/driver-track-stats`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver track stats');
  }

  return response.json();
};

export const getTrackDriverStats = async (request: TrackDriverStatsRequest): Promise<TrackDriverStatRow[]> => {
  const response = await fetch(`${API_URL}/api/stats/track-driver-stats`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      track: request.track,
      year: request.year,
      rankStat: request.rankStat ?? 'Pos',
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch track driver stats');
  }

  return response.json();
};

export const getDrivers = async (): Promise<{ name: string }[]> => {
  const response = await fetch(`${API_URL}/api/stats/drivers`, { headers: await authHeaders() });
  if (!response.ok) {
    throw new Error('Failed to fetch drivers');
  }
  return response.json();
};

export interface LatestDataInfo {
  latest_date: string;
  track: string;
  race_number: number;
}

export const getLatestDataInfo = async (): Promise<LatestDataInfo> => {
  const response = await axios.get(`${API_URL}/api/stats/latest-data`, { headers: await authHeaders() });
  return response.data;
}; 

export interface DriverMatchup {
  driver_a: string;
  driver_b: string;
  elo_a: number;
  elo_b: number;
  prob_a_wins: number;
}

export interface DriverMatchupsRequest {
  track: string;
  driver: string;
}

export const getDriverMatchups = async (request: DriverMatchupsRequest): Promise<DriverMatchup[]> => {
  const response = await fetch(`${API_URL}/api/stats/driver-matchups`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver matchups');
  }

  return response.json();
};

export interface TrackEloRanking {
  Driver: string;
  elo: number;
  elo_diff: number;
}

export interface TrackEloRankingsRequest {
  track: string;
}

export const getTrackDriverEloRankings = async (request: TrackEloRankingsRequest): Promise<TrackEloRanking[]> => {
  const response = await fetch(`${API_URL}/api/stats/track-elo-rankings`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch track driver ELO rankings');
  }

  return response.json();
};

export interface DriverTrackRecentRace {
  Date: string;
  St: number;
  Pos: number;
  Avg: number;
  DriverRating: number;
  PctT15Lap: number;
}

export interface DriverTrackRecentRacesRequest {
  track: string;
  driver: string;
  limit?: number;
}

export const getDriverTrackRecentRaces = async (
  request: DriverTrackRecentRacesRequest
): Promise<DriverTrackRecentRace[]> => {
  const response = await fetch(`${API_URL}/api/stats/driver-track-recent-races`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({
      track: request.track,
      driver: request.driver,
      limit: request.limit ?? 10,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver track recent races');
  }

  return response.json();
};

export interface AIChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AIChatUsage {
  totalTokens: number;
  promptTokens: number;
  candidateTokens: number;
}

export interface AIChatResponse {
  reply: string;
  usage?: AIChatUsage;
}

/** Gemini-backed chat that uses racing stat APIs on the server (function calling). */
export const postAIChat = async (messages: AIChatMessage[]): Promise<AIChatResponse> => {
  const response = await fetch(`${API_URL}/api/ai/chat`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ messages }),
  });

  if (response.status === 503) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'AI chat is not configured on the server (GEMINI_API_KEY).');
  }

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'Chat request failed');
  }

  return response.json();
};

export type AIChatStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'done'; reply: string; usage?: AIChatUsage }
  | { type: 'error'; message: string };

/** SSE stream (`text/event-stream`): incremental assistant text and optional tool names. */
export const postAIChatStream = async (
  messages: AIChatMessage[],
  onEvent: (ev: AIChatStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const response = await fetch(`${API_URL}/api/ai/chat/stream`, {
    method: 'POST',
    headers: {
      ...(await authHeaders()),
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ messages }),
    signal,
  });

  if (response.status === 503) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'AI chat is not configured on the server (GEMINI_API_KEY).');
  }

  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || 'Chat stream failed');
  }

  if (!response.body) {
    throw new Error('No response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const line = block.trim();
      if (!line.startsWith('data:')) {
        continue;
      }
      const jsonStr = line.slice(5).trim();
      try {
        const ev = JSON.parse(jsonStr) as AIChatStreamEvent;
        onEvent(ev);
      } catch {
        // ignore malformed chunk
      }
    }
  }
};
// ---- Monte Carlo race simulation ----

export interface SimDriverResult {
  driver: string;
  /** Rank by average simulated finish (1 = best). */
  projectedRank: number;
  winProb: number;
  top3Prob: number;
  top5Prob: number;
  top10Prob: number;
  top20Prob: number;
  avgFinish: number;
  medianFinish: number;
  /** Optimistic (10th percentile) finish. */
  p10Finish: number;
  /** Pessimistic (90th percentile) finish. */
  p90Finish: number;
  dnfRate: number;
  /** Fair American odds (no vig); null when the probability is 0. */
  fairOddsWin: number | null;
  fairOddsTop5: number | null;
  fairOddsTop10: number | null;
  nRaces: number;
  nRacesAtTrack: number;
  /** Share of simulated races finishing in each position (index 0 = 1st), and the part of it that was a DNF. */
  finishDist?: number[];
  dnfDist?: number[];
}

export interface SimTrackUsed {
  trackKey: string;
  trackType: string;
  relation: 'same_track' | 'similar' | 'same_type' | 'other';
  /** Outcome-based similarity to the selected track (0-1); null for the selected track itself. */
  similarity: number | null;
  /** 1 = most similar candidate track. */
  similarityRank: number | null;
  /** Weight multiplier before recency decay. */
  weight: number;
  races: number;
  /** Fraction (0-1) of total rating weight. */
  weightShare: number;
}

/**
 * Race chaos: how unpredictable the user expects the race to be. Normal is the model as backtested; Calm and Wild are
 * scenarios on top of it (less or more race-to-race swing, and more wrecks for Wild).
 */
export type RaceChaos = 'calm' | 'normal' | 'wild';

/** The chaos field for a request body: only sent when it isn't Normal. */
const chaosField = (chaos?: RaceChaos) => (chaos && chaos !== 'normal' ? { chaos } : {});

export interface SimulationRun {
  site: string;
  trackKey: string;
  trackType: string;
  /** Ratings use races strictly before this date. */
  asOf: string;
  generatedAt: string;
  nSims: number;
  configName: string;
  /** Track keys removed from the ratings at the user's request. */
  excludedTracks: string[];
  /** The race chaos scenario the run used (absent = normal, the backtested model). */
  chaos?: RaceChaos;
  drivers: SimDriverResult[];
  tracksUsed: SimTrackUsed[];
  /** The track-specific layer (two-layer model), simulated from the same random draws; absent if unavailable. */
  layered?: SimLayer;
  /** Present when the production model itself is the two-layer model: the breakdown behind `drivers`. */
  twoLayer?: SimTwoLayer;
}

export interface SimTwoLayer {
  configName: string;
  breakdown: SimLayerBreakdown[];
  linkedTracks: SimLinkedTrack[];
}

/** Which model a matchup or parlay uses: the production model or the track-specific layer. */
export type SimModel = 'base' | 'layered';

export interface SimLayerBreakdown {
  driver: string;
  /** Layer 1: form rating from every track in the target's family (logit-score scale). */
  formRating: number;
  /** Layer 2: how much better (+) or worse (-) the driver runs here than their form, shrunk toward 0. */
  adjustment: number;
  adjRaces: number;
  adjRacesAtTrack: number;
  adjTracks: string[];
}

export interface SimLinkedTrack {
  trackKey: string;
  specificR: number;
  specificSE: number;
}

export interface SimLayer {
  configName: string;
  drivers: SimDriverResult[];
  /** The tracks behind layer 1 (form), with their share of the form rating. */
  formTracks: SimTrackUsed[];
  breakdown: SimLayerBreakdown[];
  linkedTracks: SimLinkedTrack[];
}

const simError = async (response: Response, fallback: string): Promise<Error> => {
  const err = (await response.json().catch(() => ({}))) as { error?: string };
  return new Error(err.error || fallback);
};

/** Runs an on-demand simulation for a track (as of today). Takes ~5-15 seconds; nothing is saved. */
export const runSimulation = async (
  track: string,
  nSims: number,
  excludeTracks: string[] = [],
  layers = true,
  chaos?: RaceChaos
): Promise<SimulationRun> => {
  const response = await fetch(`${API_URL}/api/sim/run`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, excludeTracks, layers, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to run simulation');
  }
  return response.json();
};

export interface MomentumDriver {
  driver: string;
  withForm: SimDriverResult;
  withoutForm: SimDriverResult;
  /** Ratings on the model's skill scale: rating includes recent form, baseRating doesn't. */
  rating: number;
  baseRating: number;
  /** Average over the driver's last recentRaces races at this kind of track (missing if none). */
  recentRating?: number;
  recentRaces: number;
}

export interface SimulationMomentum {
  site: string;
  asOf: string;
  generatedAt: string;
  nSims: number;
  configName: string;
  excludedTracks: string[];
  /** In projected order with recent form. */
  drivers: MomentumDriver[];
}

/** A model (Base or Track Specialists) with and without its recent-form blend, from the same random draws (Momentum). */
export const runSimulationMomentum = async (
  track: string,
  nSims: number,
  excludeTracks: string[] = [],
  model: SimModel = 'base',
  chaos?: RaceChaos
): Promise<SimulationMomentum> => {
  const response = await fetch(`${API_URL}/api/sim/momentum`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, excludeTracks, model, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to run the momentum view');
  }
  return response.json();
};

/**
 * Races that fed each driver's rating in a simulation (same track, as-of date and weighting).
 * Rows match getDriverDetails plus SimWeight (share of the driver's rating) and DNF.
 */
export const getSimDriverRaces = async (
  track: string,
  asOf: string,
  drivers: string[],
  excludeTracks: string[] = [],
  model: SimModel = 'base'
): Promise<DriverDetailResponse[]> => {
  const response = await fetch(`${API_URL}/api/sim/driver-races`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, asOf, drivers, excludeTracks, model }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load race history');
  }
  return response.json();
};

/** One backtested race: the live model's predicted finish (from the races before it) and the actual finish. */
export interface ModelCheckRace {
  Driver: string;
  Date: string;
  Site: string;
  PredFinish: number;
  Top10Prob: number;
  ActualPos: number;
}

export const getSimModelCheck = async (drivers: string[], model: SimModel = 'base'): Promise<ModelCheckRace[]> => {
  const response = await fetch(`${API_URL}/api/sim/model-check`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ drivers, model }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load the model check');
  }
  return response.json();
};

export interface MatchupDriver {
  driver: string;
  /** P(finishes ahead of every other driver in the group). */
  groupWinProb: number;
  /** Average place within the group (1 = best). */
  avgGroupRank: number;
  avgFinish: number;
  winProb: number;
  top5Prob: number;
  top10Prob: number;
}

export interface MatchupHeadToHead {
  driverA: string;
  driverB: string;
  /** P(driverA finishes ahead of driverB). */
  probAAhead: number;
}

export interface SimulationMatchup {
  site: string;
  asOf: string;
  generatedAt: string;
  nSims: number;
  configName: string;
  excludedTracks: string[];
  drivers: MatchupDriver[];
  headToHead: MatchupHeadToHead[];
}

/** Re-simulates a track and returns head-to-head / group results for 2-10 drivers. Takes ~5-15 seconds. */
export const runSimulationMatchup = async (
  track: string,
  nSims: number,
  drivers: string[],
  excludeTracks: string[] = [],
  model: SimModel = 'base',
  chaos?: RaceChaos
): Promise<SimulationMatchup> => {
  const response = await fetch(`${API_URL}/api/sim/matchup`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, drivers, excludeTracks, model, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to run matchup');
  }
  return response.json();
};

/** The board scanner's head-to-heads: the matchup simulation for up to 10 drivers, on the scanner's own route. */
export const priceBoardMatchups = async (
  track: string,
  nSims: number,
  drivers: string[],
  excludeTracks: string[] = [],
  model: SimModel = 'base',
  chaos?: RaceChaos,
): Promise<SimulationMatchup> => {
  const response = await fetch(`${API_URL}/api/sim/board-scan/matchups`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, drivers, excludeTracks, model, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to price matchups');
  }
  return response.json();
};

export interface SimExplainStats {
  winProb: number;
  top5Prob: number;
  top10Prob: number;
  avgFinish: number;
  dnfRate: number;
  projectedRank: number;
  fieldSize: number;
}

/** Short AI (Gemini) explanation of why the simulation rates a driver the way it does. */
export const explainSimDriver = async (
  track: string,
  asOf: string,
  driver: string,
  stats: SimExplainStats,
  excludeTracks: string[] = []
): Promise<{ explanation: string; cached: boolean }> => {
  const response = await fetch(`${API_URL}/api/sim/explain`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, asOf, driver, stats, excludeTracks }),
  });
  if (!response.ok) {
    throw await simError(response, 'Explanation unavailable');
  }
  return response.json();
};

export interface TrackSimilarityTrack {
  trackKey: string;
  trackType: string;
  family: string;
  /** Year-to-year consistency of results at this track, r(A,A). */
  selfConsistency: number;
  /** loopdata Site for the current season, or null if the track isn't on it (e.g. Chicagoland). */
  site: string | null;
  /** How repeatable track-specific skill is here (its own track-specific correlation), or null if unknown. */
  selfSpecificR: number | null;
  selfSpecificSE: number | null;
}

export interface TrackSimilarityPair {
  trackA: string;
  trackB: string;
  /** Directed: how much a race at trackB is worth for predicting trackA (0-1). */
  similarity: number;
  /** Symmetric "character" similarity (0-1). */
  similaritySymmetric: number;
  rAB: number;
  nRacePairs: number;
  /** Track-specific similarity: correlation after removing each driver's general strength (~0 = no link). */
  specificR: number | null;
  specificSE: number | null;
  specificNRacePairs: number | null;
}

export interface TrackSimilarityMatrix {
  asOf: string;
  tracks: TrackSimilarityTrack[];
  pairs: TrackSimilarityPair[];
}

export interface TrackWeightsResult {
  site: string;
  trackKey: string;
  trackType: string;
  configName: string;
  weights: SimTrackUsed[];
  /** The config is the two-layer model: weights are the form tracks; linkedTracks feed the adjustment. */
  twoLayer: boolean;
  linkedTracks: SimLinkedTrack[];
}

/** Full outcome-based track similarity matrix (cached server-side; first load can take ~10s). */
export const getTrackSimilarity = async (): Promise<TrackSimilarityMatrix> => {
  const response = await fetch(`${API_URL}/api/sim/similarity`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, 'Failed to load track similarity');
  }
  return response.json();
};

/** Track weights the simulator would use for a track today. */
export const getTrackSimilarityWeights = async (track: string): Promise<TrackWeightsResult> => {
  const response = await fetch(`${API_URL}/api/sim/similarity/weights?track=${encodeURIComponent(track)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load track weights');
  }
  return response.json();
};

/** One leg of a same-race parlay. top_n with n = 1 is a win; ahead_of with several `others` is best of the group. */
export interface ParlayLeg {
  kind: 'top_n' | 'not_top_n' | 'ahead_of';
  driver: string;
  n?: number;
  others?: string[];
}

export interface ParlayLegResult extends ParlayLeg {
  prob: number;
  /** P(this leg | every other leg hits); null for a single leg or when the others never hit together. */
  probGivenOthers: number | null;
  /** A driver in the leg isn't in the simulated field. */
  missing: boolean;
}

export interface SimulationParlay {
  site: string;
  asOf: string;
  generatedAt: string;
  nSims: number;
  configName: string;
  excludedTracks: string[];
  legs: ParlayLegResult[];
  /** P(every leg hits), from the same simulated races, and its Monte Carlo standard error. */
  jointProb: number;
  jointSE: number;
  /** Product of the legs' own probabilities: the price if legs were independent. */
  independentProb: number;
  /** P(at least one leg hits) on the same simulated races, its standard error, and the independent version. */
  anyProb: number;
  anySE: number;
  independentAnyProb: number;
}

/** Price a same-race parlay (re-simulates the track with the same ratings as the run). */
export const priceParlay = async (
  track: string,
  legs: ParlayLeg[],
  nSims: number,
  excludeTracks: string[] = [],
  model: SimModel = 'base',
  chaos?: RaceChaos
): Promise<SimulationParlay> => {
  const response = await fetch(`${API_URL}/api/sim/parlay`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, legs, excludeTracks, model, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to price parlay');
  }
  return response.json();
};

/** One bet on a bet slip: it hits when every leg hits (one leg = a straight bet, several = a parlay). */
export interface SlipBet {
  legs: ParlayLeg[];
  /** The bet wins if any leg hits (instead of every leg). */
  any?: boolean;
}

/** One combination of bets hitting: bit b of hitMask set = bet b hit, in `sims` of the simulated races. */
export interface SlipOutcome {
  hitMask: number;
  sims: number;
}

/** A bet slip judged on one set of simulated races (odds and stakes are applied in the app). */
export interface SimulationSlip {
  site: string;
  asOf: string;
  nSims: number;
  configName: string;
  excludedTracks: string[];
  outcomes: SlipOutcome[];
  /** missing[b]: bet b names a driver who isn't in the simulated field. */
  missing: boolean[];
}

export const priceSlip = async (
  track: string,
  bets: SlipBet[],
  nSims: number,
  excludeTracks: string[] = [],
  model: SimModel = 'base',
  chaos?: RaceChaos
): Promise<SimulationSlip> => {
  const response = await fetch(`${API_URL}/api/sim/slip`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, nSims, bets, excludeTracks, model, ...chaosField(chaos) }),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to check the bet slip');
  }
  return response.json();
};

/** The signed-in user's autosaved Slip Builder slip for a track: the bets as the editor holds them and the last
 * check. `updatedAt` is the version; saves send the version they started from. */
export interface SlipDraft {
  site: string;
  bets: unknown;
  check: unknown | null;
  updatedAt: string;
}

/** GET /api/slips/draft/:site. `enabled` is false when the backend doesn't save slips (then the browser copy is all). */
export const getSlipDraft = async (site: string): Promise<{ enabled: boolean; draft: SlipDraft | null }> => {
  const response = await fetch(`${API_URL}/api/slips/draft/${encodeURIComponent(site)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) throw new Error(`Couldn't load your slip (${response.status})`);
  return response.json();
};

/** PUT /api/slips/draft/:site. On a conflict (another device saved first) returns that newer draft instead. */
export const putSlipDraft = async (
  site: string,
  body: { bets: unknown; check: unknown | null; baseUpdatedAt: string | null }
): Promise<{ conflict: boolean; draft: SlipDraft }> => {
  const response = await fetch(`${API_URL}/api/slips/draft/${encodeURIComponent(site)}`, {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (response.status === 409) return { conflict: true, draft: (await response.json()).draft };
  if (!response.ok) throw new Error(`Couldn't save your slip (${response.status})`);
  return { conflict: false, draft: (await response.json()).draft };
};

export interface ResearchArticle {
  slug: string;
  title: string;
  date: string;
  summary: string;
  tags: string[];
  order: number;
  /** Sidebar group: "Guides", "How it works" or "Analysis & case studies". */
  section: string;
  readingMinutes: number;
  /** Markdown; only present when fetching a single article. */
  body?: string;
}

/** Research articles (methodology and analysis), served by the authenticated API. */
export const getResearchArticles = async (): Promise<ResearchArticle[]> => {
  const response = await fetch(`${API_URL}/api/research`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, 'Failed to load research articles');
  }
  const data: { articles: ResearchArticle[] } = await response.json();
  return data.articles;
};

export const getResearchArticle = async (slug: string): Promise<ResearchArticle> => {
  const response = await fetch(`${API_URL}/api/research/${encodeURIComponent(slug)}`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, response.status === 404 ? 'Article not found' : 'Failed to load the article');
  }
  return response.json();
};

/** +- uncertainty (percentage points) of the production model's probabilities per market and probability bucket. */
export interface ProbUncertaintyBand {
  market: 'win' | 'top5' | 'top10';
  pLo: number;
  pHi: number;
  n: number;
  predictedPct: number;
  observedPct: number;
  calibGapPts: number;
  disagreePts: number;
  bandPts: number;
}

export const getProbUncertainty = async (): Promise<ProbUncertaintyBand[]> => {
  const response = await fetch(`${API_URL}/api/sim/uncertainty`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, 'Failed to load probability uncertainty');
  }
  const data: { bands: ProbUncertaintyBand[] } = await response.json();
  return data.bands;
};

/** A driver's one track-rank pill: this track is one of their best ("best", 1 = their best track) or worst ("worst",
 * 1 = their worst track) for a stat, among their tracks with 2+ races in the window. */
export interface TrackRankPill {
  driver: string;
  kind: 'best' | 'worst';
  stat: 'rating' | 'running' | 'finish';
  rank: number;
  of: number;
  value: number;
  races: number;
}

/** The track-rank pills for a track (one per driver at most), from the last three seasons of loop data. */
export const getTrackRankPills = async (track: string): Promise<{ since: number; pills: TrackRankPill[] }> => {
  const response = await fetch(`${API_URL}/api/sim/track-ranks?track=${encodeURIComponent(track)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load track ranks');
  }
  return response.json();
};

/** One of a driver's tracks (2+ races in the window) with their averages and its rank on each stat (1 = best). */
export interface DriverTrackRank {
  site: string;
  races: number;
  rating: number;
  running: number;
  finish: number;
  ratingRank: number;
  runningRank: number;
  finishRank: number;
}

/** Every track a driver has 2+ races at in the track-rank window, ranked the same way as the pills. */
export const getDriverTrackRanks = async (driver: string): Promise<{ since: number; tracks: DriverTrackRank[] }> => {
  const response = await fetch(`${API_URL}/api/sim/track-ranks/driver?driver=${encodeURIComponent(driver)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, "Failed to load the driver's tracks");
  }
  return response.json();
};

/** One race behind a streak pill. */
export interface StreakRace {
  site: string;
  date: string;
  /** 0 when the start isn't known. */
  start: number;
  finish: number;
  /** Average running position in the race (0 when missing). */
  running: number;
  /** Driver rating in the race (0 when missing). */
  rating: number;
}

/** A driver's last races at a track and the tracks most like it, with their streak pill (a run of top 5s or top
 * 10s) when they have one. */
export interface DriverStreak {
  driver: string;
  /** The pill; absent when the driver has none. */
  kind?: 'top5' | 'top10';
  /** Top 5s (or top 10s) in the window, for the pill. */
  count?: number;
  /** Races in the window (up to `window`; a pill needs a full window). */
  of: number;
  top5: number;
  top10: number;
  /** Newest first. */
  races: StreakRace[];
}

export interface TrackStreaks {
  track: string;
  since: number;
  /** The track, then its most similar tracks. */
  tracks: string[];
  window: number;
  /** Every driver with races there. */
  drivers: DriverStreak[];
}

/** Every driver's last races at a track and its most similar tracks, with streak pills ("T5 · 7/10"). */
export const getTrackStreaks = async (track: string): Promise<TrackStreaks> => {
  const response = await fetch(`${API_URL}/api/sim/streaks?track=${encodeURIComponent(track)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load streaks');
  }
  return response.json();
};

/** Short AI (Gemini) summary of a head-to-head / group matchup: who's favored among the chosen drivers and why. */
export const explainSimMatchup = async (
  track: string,
  asOf: string,
  matchup: SimulationMatchup,
  excludeTracks: string[] = [],
  model: SimModel = 'base'
): Promise<{ explanation: string; cached: boolean }> => {
  const response = await fetch(`${API_URL}/api/sim/matchup-explain`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ track, asOf, model, excludeTracks, drivers: matchup.drivers, headToHead: matchup.headToHead }),
  });
  if (!response.ok) {
    throw await simError(response, 'Summary unavailable');
  }
  return response.json();
};

// ---- Account (Profile page) ----

/** The signed-in user's account. managementEnabled is false until the server has Auth0 Management API access. */
export interface AccountProfile {
  managementEnabled: boolean;
  email: string;
  emailVerified: boolean;
  name: string;
  picture: string;
  /** "password" for email and password accounts, otherwise the social provider (e.g. "google"). */
  provider: string;
  canChangePassword: boolean;
  canChangeEmail: boolean;
  canChangeName: boolean;
  createdAt?: string;
  lastLogin?: string;
  loginsCount?: number;
}

const accountRequest = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const response = await fetch(`${API_URL}/api/account${path}`, {
    method,
    headers: await authHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw await simError(response, 'Something went wrong');
  }
  return response.json();
};

export const getAccount = () => accountRequest<AccountProfile>('GET', '');
export const updateAccount = (changes: { name?: string; email?: string }) =>
  accountRequest<AccountProfile>('PUT', '', changes);
export const sendPasswordReset = () => accountRequest<{ sent: boolean; email: string }>('POST', '/password-reset');
export const resendVerificationEmail = () => accountRequest<{ sent: boolean }>('POST', '/verify-email');
export const deleteAccount = () => accountRequest<{ deleted: boolean }>('DELETE', '', { confirm: 'DELETE' });

// ---- Board scanner ----

export type BoardLineStatus = 'ok' | 'unverified' | 'disagree' | 'suspect' | 'unmatched' | 'unpriced' | 'unclear';

/** One line read from a sportsbook screenshot, checked by the server (both reads agree, sanity checks). */
export interface BoardScanLine {
  /** 'unknown' when no market was shown and the prices didn't clearly fit one (status 'unclear'). */
  market: 'win' | 'top3' | 'top5' | 'top10' | 'h2h' | 'other' | 'unknown';
  marketLabel: string;
  driverRaw: string;
  /** Matched field name, when the driver is in the simulated field. */
  driver?: string;
  opponent?: string;
  odds: number;
  oddsRead2?: number;
  status: BoardLineStatus;
  reason?: string;
  /** Our probabilities for this market, as sent with the scan. */
  probBase?: number;
  probLayered?: number;
  /** How the market was known: printed in the screenshot, inferred from the prices, or picked by the user. */
  marketBasis?: 'printed' | 'inferred' | 'cautious' | 'picked';
  /** The market printed on the board, when its prices looked like another one (priced cautiously). */
  printedMarket?: string;
  /** The board section the line came from, so a whole section's market can be changed at once. */
  group?: string;
  /** The likeliest markets, when the market is unclear or the printed one looks wrong. */
  candidates?: string[];
}

export interface BoardScanResult {
  scanId: string;
  book?: string;
  lines: BoardScanLine[];
  counts: Partial<Record<BoardLineStatus, number>>;
  imagesSaved: boolean;
}

/** Upload one to four board screenshots; the server reads them and returns checked lines with our probabilities. */
export const scanBoard = async (
  images: File[],
  run: { site: string; asOf: string; configName: string },
  field: {
    driver: string;
    base: Record<string, number>;
    layered?: Record<string, number>;
    /** Book-mode probabilities: used only to tell which market an unlabeled part of the board is. */
    book?: Record<string, number>;
  }[],
): Promise<BoardScanResult> => {
  const form = new FormData();
  images.forEach((f) => form.append('images', f));
  form.append('track', run.site);
  form.append('asOf', run.asOf);
  form.append('config', run.configName);
  form.append('field', JSON.stringify(field));
  const headers = await authHeaders();
  delete headers['Content-Type']; // the browser sets the multipart boundary
  const response = await fetch(`${API_URL}/api/sim/board-scan`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!response.ok) {
    throw await simError(response, "Couldn't read that screenshot");
  }
  return response.json();
};

/** One of the signed-in user's saved board scans. */
export interface BoardScanSummary {
  scanId: string;
  scannedAt: string;
  site: string;
  asOf?: string;
  book?: string;
  nLines: number;
  nPriced: number;
}

/** A saved scan, rebuilt as a scan result (prices and our probabilities as saved at scan time). */
export interface BoardScanDetail extends BoardScanResult {
  scannedAt: string;
  site: string;
  asOf?: string;
}

/** The signed-in user's saved scans, newest first. */
export const listBoardScans = async (): Promise<BoardScanSummary[]> => {
  const response = await fetch(`${API_URL}/api/sim/board-scans`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, "Couldn't load your previous scans");
  }
  const body = await response.json();
  return body.scans ?? [];
};

/** One of the signed-in user's saved scans. */
export const getBoardScan = async (scanId: string): Promise<BoardScanDetail> => {
  const response = await fetch(`${API_URL}/api/sim/board-scans/${encodeURIComponent(scanId)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, "Couldn't load that scan");
  }
  return response.json();
};

/** One of the signed-in user's saved Simulator runs. */
export interface SimRunSummary {
  runId: string;
  createdAt: string;
  site: string;
  asOf?: string;
  configName?: string;
  nSims: number;
  excludedTracks: string[];
  hasLayered: boolean;
  /** The run's race chaos scenario (absent = normal). */
  chaos?: RaceChaos;
}

/** The signed-in user's saved Simulator runs, newest first. */
export const listSimRuns = async (): Promise<SimRunSummary[]> => {
  const response = await fetch(`${API_URL}/api/sim/runs`, { headers: await authHeaders() });
  if (!response.ok) {
    throw await simError(response, "Couldn't load your previous simulations");
  }
  const body = await response.json();
  return body.runs ?? [];
};

/** One of the signed-in user's saved runs, exactly as it was returned at the time. */
export const getSimRun = async (runId: string): Promise<{ runId: string; createdAt: string; run: SimulationRun }> => {
  const response = await fetch(`${API_URL}/api/sim/runs/${encodeURIComponent(runId)}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, "Couldn't load that simulation");
  }
  return response.json();
};

/** One pit stop: the lap the car came in, its position going in and out, and positions gained (negative: lost). */
export interface LivePitStop {
  lap: number;
  inPos: number;
  outPos: number;
  gained: number;
  caution: boolean;
  /** Tires changed (0-4; -1 = not known yet), seconds on pit road and stopped in the box (from the pit feed). */
  tires: number;
  pitRoad?: number;
  box?: number;
}

/** The race's pit picture: a green-flag cycle (who's pitted, who hasn't), a caution's strategy split, every stop. */
export interface LivePit {
  phase: 'green_cycle' | 'caution' | 'none';
  /** The order the chances are simulated from. */
  basis: 'running' | 'cycled' | 'caution_blend';
  cycleStartLap?: number;
  pitted?: string[];
  toPit?: string[];
  /** Seconds a green-flag stop costs (measured this race, or an estimate). */
  pitLoss: number;
  measured: boolean;
  cautionLap?: number;
  stayedOut?: string[];
  pittedUnder?: string[];
  fourTires?: string[];
  twoTires?: string[];
  noTires?: string[];
  /** Every stop this race, newest first. */
  stops: (LivePitStop & { driver: string; number: string })[];
}

/** One car in the live race: where it is and its chances from here. */
export interface LiveDriver {
  driver: string;
  number: string;
  pos: number;
  start: number;
  /** Seconds behind the leader (cars on the lead lap). */
  gap: number;
  lapsCompleted: number;
  lapsDown: number;
  out: boolean;
  /** Off track under the damaged vehicle policy (being repaired; may return). */
  inGarage?: boolean;
  win: number;
  top3: number;
  top5: number;
  top10: number;
  /** Pre-race win chance (the live model at the green flag). */
  preWin: number;
  /** False for a driver we don't rate (simulated as a back-marker). */
  rated: boolean;
  stops?: LivePitStop[];
  /** Place, and seconds behind the new leader, once the green-flag pit cycle plays out (absent when no cycle is on). */
  cycledPos?: number;
  cycledGap?: number;
  /** Recent green-flag lap times (s) and closing on the car ahead (absent until a few laps have been seen). */
  pace?: {
    last5?: number;
    last10?: number;
    run?: number;
    runLaps?: number;
    /** Seconds a lap gained on the car ahead over the last 5 laps (negative: losing). */
    closing?: number;
    /** Laps to catch the car ahead at that rate. */
    catchIn?: number;
    /** Last lap the pace covers (under caution: the last green lap before it). */
    toLap?: number;
  };
}

/** GET /api/live: the race on now (beta), or { live: false }. */
export interface LiveRace {
  live: boolean;
  track?: string;
  site?: string;
  raceId?: number;
  lap?: number;
  laps?: number;
  flag?: 'green' | 'caution' | 'red' | 'white' | 'checkered' | 'other';
  finished?: boolean;
  cautions?: [number, number][];
  stageEnds?: number[];
  asOf?: string;
  nSims?: number;
  /** Typical current lap in seconds (front-runners' last laps; slower under caution). */
  lapTime?: number;
  updatedAt: string;
  drivers?: LiveDriver[];
  history?: { lap: number; win: Record<string, number> }[];
  pit?: LivePit;
  message?: string;
}

export const getLiveRace = async (): Promise<LiveRace> => {
  const response = await fetch(`${API_URL}/api/live`, { headers: await authHeaders() });
  // An older backend without the tracker: treat as no race on.
  if (response.status === 404) return { live: false, updatedAt: new Date().toISOString() };
  if (!response.ok) throw new Error(`Couldn't read the live race (${response.status})`);
  return response.json();
};

/** POST /api/live/slip: the slip's bets judged on the live simulation (the same outcomes shape as priceSlip). */
export interface LiveSlip {
  live: boolean;
  site?: string;
  lap?: number;
  nSims?: number;
  outcomes: SlipOutcome[];
  missing: boolean[];
}

export const priceLiveSlip = async (bets: SlipBet[]): Promise<LiveSlip> => {
  const response = await fetch(`${API_URL}/api/live/slip`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ bets }),
  });
  if (!response.ok) throw new Error(`Couldn't price your slip live (${response.status})`);
  return response.json();
};

// ---- Admin page (read-only; needs the read:admin permission) ----

export interface AdminUser {
  hash: string;
  admin: boolean;
  firstSeen: string;
  lastSeen: string;
  sessions: number;
  requests: number;
  simulations: number;
  scans: number;
  aiCalls: number;
  bigQueryBytes: number;
  bigQueryUSD: number;
  geminiUSD: number;
  costUSD: number;
  email?: string;
  joined?: string;
  provider?: string;
}

export interface AdminRoute {
  route: string;
  calls: number;
  users: number;
  bigQueryBytes: number;
  bigQueryUSD: number;
  geminiIn: number;
  geminiOut: number;
  geminiUSD: number;
  p50ms: number;
  p95ms: number;
  errors: number;
}

export interface AdminEvent {
  event_time: string;
  kind: 'api' | 'rum';
  user_hash?: string;
  is_admin?: boolean;
  session_id?: string;
  route?: string;
  method?: string;
  status?: number;
  latency_ms?: number;
  name?: string;
  detail?: string;
  page?: string;
  gemini_model?: string;
  gemini_in?: number;
  gemini_out?: number;
}

export interface AdminSummary {
  days: number;
  generatedAt: string;
  activeUsers: number;
  activeToday: number;
  active7d: number;
  accounts: number;
  signups: number;
  totals: {
    simulations: number;
    scans: number;
    aiCalls: number;
    errors: number;
    bigQueryUSD: number;
    geminiUSD: number;
    geminiIn: number;
    geminiOut: number;
  };
  daily: { date: string; bigQueryUSD: number; systemUSD: number; geminiUSD: number; users: number }[];
  users: AdminUser[];
  routes: AdminRoute[];
  system: { bigQueryBytes: number; bigQueryUSD: number };
  features: { name: string; detail: string; users: number; events: number }[];
  loads: { detail: string; n: number; p50ms: number; p95ms: number }[];
  recent: AdminEvent[];
}

/** Whether the signed-in user can open the admin page (false for everyone else, without an error). */
export const getAdminMe = async (): Promise<boolean> => {
  const response = await fetch(`${API_URL}/api/admin/me`, { headers: await authHeaders() });
  return response.ok;
};

/** The admin page's data; fresh skips the server's 2-minute cache (the Refresh button). */
/** One saved board scan's outcome, for the admin page's Board scanner tab. */
export interface AdminScan {
  scanId: string;
  scannedAt: string;
  userHash?: string;
  site?: string;
  book?: string;
  geminiModel?: string;
  nImages: number;
  nLines: number;
  nPriced: number;
  /** Lines per status (ok, unverified, disagree, unmatched, unpriced, unclear, suspect). */
  statuses: Record<string, number>;
}

export interface AdminScanHealth {
  days: number;
  since: string;
  scans: AdminScan[];
}

export const getAdminBoardScans = async (
  days: number,
  fresh = false,
): Promise<{ health: AdminScanHealth; emails: Record<string, string> }> => {
  const response = await fetch(`${API_URL}/api/admin/board-scans?days=${days}${fresh ? '&fresh=1' : ''}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load board scans');
  }
  return response.json();
};

export const getAdminSummary = async (
  days: number,
  fresh = false,
): Promise<{ summary: AdminSummary; emails: Record<string, string> }> => {
  const response = await fetch(`${API_URL}/api/admin/summary?days=${days}${fresh ? '&fresh=1' : ''}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load usage');
  }
  return response.json();
};

export const getAdminTimeline = async (hash: string, days: number): Promise<AdminEvent[]> => {
  const response = await fetch(`${API_URL}/api/admin/users/${encodeURIComponent(hash)}/timeline?days=${days}`, {
    headers: await authHeaders(),
  });
  if (!response.ok) {
    throw await simError(response, 'Failed to load the timeline');
  }
  return (await response.json()).events;
};
